import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function SignupPage() {
  const [formData, setFormData] = useState({
    empresa: '',
    nombreCompleto: '',
    email: '',
    usuario: '',
    contraseña: '',
    confirmarContraseña: '',
  });
  const [error, setError] = useState('');
  const { signup, isLoading } = useAuth();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.contraseña !== formData.confirmarContraseña) {
      setError('Passwords do not match');
      return;
    }

    try {
      await signup({
        empresa: formData.empresa,
        nombreCompleto: formData.nombreCompleto,
        email: formData.email,
        usuario: formData.usuario,
        contraseña: formData.contraseña,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-4">
            <img src="/qpulse-logo.png" alt="QPulseMES" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold">QPulseMES</span>
          </Link>
          <CardTitle className="text-2xl">{t('auth.signupTitle')}</CardTitle>
          <CardDescription>
            Create your company account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                name="empresa"
                type="text"
                placeholder="Company Name"
                value={formData.empresa}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Input
                name="nombreCompleto"
                type="text"
                placeholder={t('app.fullName')}
                value={formData.nombreCompleto}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Input
                name="email"
                type="email"
                placeholder={t('common.email')}
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Input
                name="usuario"
                type="text"
                placeholder={t('app.username')}
                value={formData.usuario}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Input
                name="contraseña"
                type="password"
                placeholder={t('auth.password')}
                value={formData.contraseña}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Input
                name="confirmarContraseña"
                type="password"
                placeholder={t('auth.confirmPassword')}
                value={formData.confirmarContraseña}
                onChange={handleChange}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('auth.signup')}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:underline">
                {t('auth.login')}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}